import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import ContentfulPreOrderModal from '@src/quince-site-common/src/domains/contentful/ContentfulPreOrder'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { default as Button } from '../button'
import Icon from '../icon'
import { default as Modal } from '../modal'
import CalendarIcon from '../productForm/icons/calendar.inline.svg'

import * as dsStyles from './pre-order-message-ds.styles'
import styles from './preOrderMessage.module.scss'

import type { Document } from '@contentful/rich-text-types'

dayjs.extend(advancedFormat)

interface PreOrderInfoPopupType {
  preOrderModalData?: ContentfulPreOrderModal
  showPopup: boolean
  setShowPopup: (val: boolean) => void
}

interface PreOrderPopupQnAType {
  question: string | null
  answer: string | Document | undefined
}

interface PropType {
  preOrderModalData?: ContentfulPreOrderModal
  preOrderShippingDate?: string
  selectedVariantId?: number
  isQuickATCModal?: boolean
}
// eslint-disable-next-line
const PREORDER_DATE_FORMAT_V2 = 'MM/DD'

function PreOrderPopupQnA({ question, answer }: PreOrderPopupQnAType) {
  return (
    <div className={styles.qnaItemWrapper}>
      <div className={styles.question}>{question}</div>
      <div className={styles.answer}>
        {answer && documentToReactComponents(typeof answer === 'string' ? JSON.parse(answer) : answer)}
      </div>
    </div>
  )
}

function PreOrderInfoPopup({ preOrderModalData, showPopup, setShowPopup }: PreOrderInfoPopupType) {
  function handleBtnClick() {
    handleClose()
  }

  function handleClose() {
    setShowPopup(false)
  }

  return (
    <Modal
      open={showPopup}
      className={styles.preOrderModal}
      modalWrapperClassName={styles.preOrderModalWrapper}
      showCloseButton={true}
      onClose={handleClose}
    >
      <div className={styles.preOrderPopupWrapper}>
        <h2 className={styles.header}>{preOrderModalData?.displayTitle}</h2>
        <div>
          {preOrderModalData?.genericContents.map(data => {
            const c = data.content?.content
            const answer = c && 'raw' in c ? c.raw : (c as unknown as Document | undefined)
            return <PreOrderPopupQnA key={data.id} question={data.title} answer={answer} />
          })}
        </div>
        {preOrderModalData?.buttons.map(btn => (
          <Button
            key={btn.id}
            type={btn.type}
            size={btn.size}
            className={styles.gotItBtn}
            round={btn.rounded}
            onClick={handleBtnClick}
          >
            {btn.title}
          </Button>
        ))}
      </div>
    </Modal>
  )
}

function PreOrderMessage({ selectedVariantId, preOrderModalData, preOrderShippingDate, isQuickATCModal }: PropType) {
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const { t } = useTranslation('common')
  const isDS = shouldUseDesignSystem()
  const onInfoBtnClick = () => {
    trackEvent(TELEMETRY_EVENT_NAMES.PREORDER_INFO_CLICK, {
      variantId: selectedVariantId,
    })
    if (!preOrderModalData) {
      console.warn('Pre order data is not available')
      return
    }
    setShowPopup(true)
  }
  return (
    <div>
      <div className={isDS ? dsStyles.preorderMessageWrapperDsClass : styles.preOrderMessageWrapperNewDesignV2}>
        <Icon
          iconSvg={CalendarIcon}
          className={
            isDS ? dsStyles.preorderCalendarIconDsClass : styles.preOrderMessageWrapperNewDesignV2__calendarIcon
          }
        />
        <p className={isDS ? undefined : styles.preOrderMessageWrapperNewDesignV2__text}>
          {t('ready_for_preorder', { date: dayjs(preOrderShippingDate).format(PREORDER_DATE_FORMAT_V2) })}
        </p>
        {!isQuickATCModal && (
          <p
            onClick={onInfoBtnClick}
            className={
              isDS ? dsStyles.preorderDetailsTextDsClass : styles.preOrderMessageWrapperNewDesignV2__detailsText
            }
          >
            {t('details')}
          </p>
        )}
      </div>
      <PreOrderInfoPopup preOrderModalData={preOrderModalData} showPopup={showPopup} setShowPopup={setShowPopup} />
    </div>
  )
}

export default PreOrderMessage
